import React, { useState, useEffect } from 'react';
import { ApiDetails, SearchParameterType } from '../../types';
import { apiService } from '../../services/apiService';
import { apiAdmin } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface APITestModalProps {
  api: ApiDetails | null;
  isOpen: boolean;
  onClose: () => void;
}

interface TestResult {
  success: boolean;
  api_name: string;
  search_value: string;
  request_url: string;
  request_headers: Record<string, any>;
  request_body: any;
  response_status: number;
  response_time: number;
  result: any;
  error?: string;
  cost: number;
}

export const APITestModal: React.FC<APITestModalProps> = ({
  api,
  isOpen,
  onClose
}) => {
  const { showToast } = useToast();
  const [parameterTypes, setParameterTypes] = useState<SearchParameterType[]>([]);
  const [selectedParameterType, setSelectedParameterType] = useState<SearchParameterType | null>(null);
  const [testValue, setTestValue] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingParameterTypes, setIsLoadingParameterTypes] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [requestPreview, setRequestPreview] = useState<{
    method?: string;
    url?: string;
    complete_url?: string;
    headers?: Record<string, string>;
    data?: Record<string, unknown>;
    body?: Record<string, unknown>;
    params?: Record<string, unknown>;
    error?: string;
    note?: string;
  } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isAutoMapping, setIsAutoMapping] = useState(false);

  // Load parameter types on mount
  useEffect(() => {
    const loadParameterTypes = async () => {
      try {
        setIsLoadingParameterTypes(true);
        const types = await apiService.getParameterTypes();
        setParameterTypes(types);
      } catch (error) {
        console.error('Failed to load parameter types:', error);
        showToast('Failed to load parameter types', 'error');
      } finally {
        setIsLoadingParameterTypes(false);
      }
    };

    if (isOpen) {
      loadParameterTypes();
    }
  }, [isOpen, showToast]);

  // Reset form when API changes
  useEffect(() => {
    if (api) {
      setTestValue('');
      setCountryCode('');
      setTestResult(null);
      setRequestPreview(null);
      setErrors({});
    }
  }, [api]);

  // Set selected parameter type when API changes
  useEffect(() => {
    if (api && parameterTypes.length > 0) {
      // Try to find parameter type by ID - check both camelCase and snake_case versions
      const searchParamTypeId = api.searchParameterTypeId || 
                                (api as any).search_parameter_type_id || 
                                (api as any).search_parameter_type?.id;
      
      console.log('APITestModal - Finding parameter type:', {
        api: api.name,
        searchParamTypeId,
        availableParameterTypes: parameterTypes.map(t => ({ id: t.id, name: t.name, display_name: t.display_name }))
      });
      
      const paramType = parameterTypes.find(t => t.id === searchParamTypeId);
      console.log('APITestModal - Found parameter type:', paramType);
      
      // If no parameter type found by ID, try to find by name from API data
      let fallbackParamType = null;
      if (!paramType && (api as any).search_parameter_type?.name) {
        fallbackParamType = parameterTypes.find(t => t.name === (api as any).search_parameter_type.name);
        console.log('APITestModal - Fallback parameter type found:', fallbackParamType);
      }
      
      const finalParamType = paramType || fallbackParamType;
      setSelectedParameterType(finalParamType || null);
      
      // Set default country code for phone numbers (same logic as SearchPage)
      if (finalParamType?.name === 'phone_number' && finalParamType.country_codes && finalParamType.country_codes.length > 0) {
        const indiaCode = finalParamType.country_codes.find(cc => cc.country.includes('India'));
        if (indiaCode) {
          setCountryCode(indiaCode.code);
        }
      } else {
        setCountryCode('');
      }
      
      // Set default test value based on parameter type
      if (finalParamType) {
        const defaultValues: Record<string, string> = {
          'phone_number': '9876543210',
          'vehicle_number': 'MH12AB1234',
          'ip_address': '192.168.1.1',
          'website': 'https://example.com',
          'email': 'test@example.com',
          'ifsc': 'SBIN0001234',
          'imei': '123456789012345',
          'company_id': 'A12345BC6789DEF123456',
          'director_id': '12345678',
          'voter_id': 'ABC1234567',
          'individual_pan': 'ABCDE1234F',
          'username': 'testuser123',
          'social_media_handle': '@testuser',
          'cryptocurrency_address': '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa',
          'domain': 'example.com'
        };
        
        if (defaultValues[finalParamType.name]) {
          setTestValue(defaultValues[finalParamType.name]);
        }
      }
    }
  }, [api, parameterTypes]);

  // Get placeholder text based on selected parameter type (same logic as SearchPage)
  const getPlaceholderText = (): string => {
    if (!selectedParameterType) return 'Enter test value...';
    
    // If country code is selected, show a more specific placeholder
    const hasCountryCodes = selectedParameterType && Array.isArray(selectedParameterType.country_codes) && selectedParameterType.country_codes.length > 0;
    if (hasCountryCodes && countryCode && selectedParameterType.name === 'phone_number') {
      return 'Enter phone number (10-11 digits)';
    }
    
    const placeholders: Record<string, string> = {
      'phone_number': '10-11 digit phone number',
      'vehicle_number': 'MH12AB1234',
      'ip_address': '192.168.1.1',
      'website': 'https://example.com',
      'email': 'user@example.com',
      'ifsc': 'SBIN0001234',
      'imei': '123456789012345',
      'company_id': 'A12345BC6789DEF123456',
      'director_id': '12345678',
      'voter_id': 'ABC1234567',
      'individual_pan': 'ABCDE1234F',
      'username': 'testuser123',
      'social_media_handle': '@username',
      'cryptocurrency_address': '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa',
      'domain': 'example.com'
    };
    
    return placeholders[selectedParameterType.name] || 'Enter test value...';
  };

  // Validate test value
  const validateTestValue = (value: string): string => {
    if (!selectedParameterType) return '';
    if (!value.trim()) return '';
    
    // For phone numbers with country code
    if (selectedParameterType.name === 'phone_number' && countryCode) {
      if (!/^\d{10}$/.test(value.trim())) {
        return 'Please enter a valid 10-digit phone number';
      }
      return '';
    }
    
    try {
      const regex = new RegExp(selectedParameterType.validation_regex);
      if (!regex.test(value)) {
        return selectedParameterType.validation_error_message;
      }
    } catch (error) {
      console.error('Invalid regex pattern:', selectedParameterType.validation_regex);
    }
    
    return '';
  };

  // Handle test value change
  const handleTestValueChange = (value: string) => {
    setTestValue(value);
    
    // Clear error when user starts typing
    if (errors.testValue) {
      setErrors(prev => ({ ...prev, testValue: '' }));
    }
    
    // Real-time validation
    const error = validateTestValue(value);
    if (error) {
      setErrors(prev => ({ ...prev, testValue: error }));
    }
  };

  // Test the API
  const handleTestAPI = async () => {
    if (!api || !selectedParameterType) {
      showToast('Please select an API and parameter type', 'error');
      return;
    }

    // Validate test value
    const validationError = validateTestValue(testValue);
    if (validationError) {
      setErrors({ testValue: validationError });
      return;
    }

    setIsLoading(true);
    setTestResult(null);
    setErrors({});

    try {
      // Use test value as-is, pass country_code separately
      const finalTestValue = testValue.trim();
      
      console.log('Testing specific API:', {
        apiId: api.id,
        apiName: api.name,
        testValue: finalTestValue,
        countryCode: countryCode,
        parameterType: selectedParameterType.name
      });

      // Call the specific API test endpoint (not the general search)
      const result = await apiAdmin.testApi(api.id!, finalTestValue, true, countryCode); // true = use test mode (no credit deduction)
      
      console.log('API test result:', result);

      if (result.success) {
        setTestResult({
          success: true,
          api_name: api.name,
          search_value: finalTestValue,
          request_url: result.request?.url || '',
          request_headers: result.request?.headers || {},
          request_body: result.request?.body || {},
          response_status: result.response?.status || 200,
          response_time: result.response_time || 0,
          result: result.response?.data || result.data,
          error: undefined,
          cost: api.price
        });
        showToast('API test completed successfully!', 'success');
      } else {
        setTestResult({
          success: false,
          api_name: api.name,
          search_value: finalTestValue,
          request_url: result.request?.url || '',
          request_headers: result.request?.headers || {},
          request_body: result.request?.body || {},
          response_status: result.response?.status || 0,
          response_time: result.response_time || 0,
          result: null,
          error: result.error || result.message || 'API test failed',
          cost: api.price
        });
        showToast(result.error || result.message || 'API test failed', 'error');
      }

    } catch (error: any) {
      console.error('API test failed:', error);
      showToast(`API test failed: ${error.message}`, 'error');
      
      setTestResult({
        success: false,
        api_name: api.name,
        search_value: testValue,
        request_url: '',
        request_headers: {},
        request_body: {},
        response_status: 0,
        response_time: 0,
        result: null,
        error: error.message,
        cost: api.price
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAutoMapFields = async () => {
    if (!api?.id) return;
    setIsAutoMapping(true);
    try {
      const responseData = (testResult as any)?.result?.data
        ?? (testResult as any)?.response?.data;
      const result = await apiAdmin.autoMapFields(api.id, {
        testValue: testValue.trim() || undefined,
        countryCode: countryCode || undefined,
        responseData,
        save: true,
      });
      if (result.success) {
        showToast(`Mapped ${result.fields?.length || 0} response fields`, 'success');
      } else {
        showToast(result.message || 'Auto-map failed', 'error');
      }
    } catch (err: any) {
      showToast(err.message || 'Auto-map failed', 'error');
    } finally {
      setIsAutoMapping(false);
    }
  };

  // Preview request (build URL + headers + body without sending)
  const handlePreviewRequest = async () => {
    if (!api || !selectedParameterType) {
      showToast('Please select an API and parameter type', 'error');
      return;
    }
    const validationError = validateTestValue(testValue);
    if (validationError) {
      setErrors({ testValue: validationError });
      return;
    }
    setPreviewLoading(true);
    setRequestPreview(null);
    setErrors({});
    try {
      const res = await apiAdmin.previewApiRequest(api.id!, testValue.trim(), countryCode || undefined);
      const req = res?.request;
      if (req?.error && !req?.url && !req?.complete_url) {
        setErrors({ preview: req.error });
        setRequestPreview(null);
      } else {
        setRequestPreview(req || null);
      }
    } catch (err: any) {
      showToast(err.message || 'Failed to build request', 'error');
      setRequestPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Send request (after preview)
  const handleSendRequest = async () => {
    if (!api || !selectedParameterType) return;
    const validationError = validateTestValue(testValue);
    if (validationError) {
      setErrors({ testValue: validationError });
      return;
    }
    await handleTestAPI();
  };

  // Reset form when modal closes
  const handleClose = () => {
    setTestValue('');
    setCountryCode('');
    setTestResult(null);
    setRequestPreview(null);
    setErrors({});
    onClose();
  };

  if (!isOpen || !api) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Test API: {api.name}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:text-gray-300 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            Test this specific API with different parameter values. This will only test the selected API, not all APIs for this parameter type.
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* API Info */}
            <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">API Configuration</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Method:</span> {api.requestMethod}
                </div>
                <div>
                  <span className="font-medium">Parameter Format:</span> {api.parameterFormat}
                </div>
                <div>
                  <span className="font-medium">URL Pattern:</span> {api.urlPattern}
                </div>
                <div>
                  <span className="font-medium">Cost:</span> {api.price} coins
                </div>
              </div>
            </div>

            {/* Test Form */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Test Parameters</h3>
              
              {/* Parameter Type (Read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Parameter Type
                </label>
                <input
                  type="text"
                  value={
                    isLoadingParameterTypes 
                      ? 'Loading parameter types...' 
                      : selectedParameterType?.display_name || 
                        (api as any).search_parameter_type?.display_name || 
                        'Parameter type not found'
                  }
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300"
                />
                {selectedParameterType && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Type: {selectedParameterType.name} | ID: {selectedParameterType.id} | Validation: {selectedParameterType.validation_regex}
                  </p>
                )}
                {api && (
                  <div className="text-xs text-blue-500 mt-1">
                    <p>API Search Parameter Type ID: {api.searchParameterTypeId || 'undefined'}</p>
                    <p>Snake Case ID: {(api as any).search_parameter_type_id || 'undefined'}</p>
                    <p>Nested ID: {(api as any).search_parameter_type?.id || 'undefined'}</p>
                    <p>Found ID: {api.searchParameterTypeId || (api as any).search_parameter_type_id || (api as any).search_parameter_type?.id || 'none'}</p>
                  </div>
                )}
              </div>

              {/* Country Code for Phone Numbers */}
              {selectedParameterType?.name === 'phone_number' && selectedParameterType.country_codes && selectedParameterType.country_codes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                    Country Code
                  </label>
                  <Select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="w-full"
                  >
                    <option value="">Select country code</option>
                    {selectedParameterType.country_codes.map((cc, index) => (
                      <option key={index} value={cc.code}>
                        {cc.country} ({cc.code})
                      </option>
                    ))}
                  </Select>
                </div>
              )}

              {/* Test Value */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Test Value *
                </label>
                <Input
                  type="text"
                  value={testValue}
                  onChange={(e) => handleTestValueChange(e.target.value)}
                  placeholder={getPlaceholderText()}
                  className="w-full"
                  disabled={isLoading}
                />
                {errors.testValue && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.testValue}</p>
                )}
              </div>

              {/* Preview request & Send buttons */}
              <div className="flex flex-wrap gap-2 justify-end">
                <Button
                  onClick={handlePreviewRequest}
                  disabled={previewLoading || !testValue.trim()}
                  variant="outline"
                  className="px-6 py-2"
                >
                  {previewLoading ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Building request...
                    </>
                  ) : (
                    'Preview request'
                  )}
                </Button>
                <Button
                  onClick={handleSendRequest}
                  disabled={isLoading || !testValue.trim()}
                  className="px-6 py-2"
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Sending...
                    </>
                  ) : (
                    'Send request'
                  )}
                </Button>
              </div>

              {/* Request preview: URL, headers, body before sending */}
              {errors.preview && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
                  {errors.preview}
                </div>
              )}
              {requestPreview && !requestPreview.error && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2 font-medium text-gray-900 dark:text-white">
                    Request to be sent
                  </div>
                  <div className="p-4 space-y-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-600 dark:text-gray-300 block mb-1">Method</span>
                      <span className="font-mono bg-gray-50 dark:bg-gray-900 px-2 py-1 rounded">{requestPreview.method || 'GET'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600 dark:text-gray-300 block mb-1">URL (base)</span>
                      <pre className="bg-gray-50 dark:bg-gray-900 p-3 rounded overflow-x-auto break-all font-mono text-xs">{requestPreview.url || '-'}</pre>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600 dark:text-gray-300 block mb-1">Complete URL (with query if GET)</span>
                      <pre className="bg-gray-50 dark:bg-gray-900 p-3 rounded overflow-x-auto break-all font-mono text-xs">{requestPreview.complete_url || requestPreview.url || '-'}</pre>
                    </div>
                    {requestPreview.headers && Object.keys(requestPreview.headers).length > 0 && (
                      <div>
                        <span className="font-medium text-gray-600 dark:text-gray-300 block mb-1">Headers</span>
                        <pre className="bg-gray-50 dark:bg-gray-900 p-3 rounded overflow-x-auto font-mono text-xs">{JSON.stringify(requestPreview.headers, null, 2)}</pre>
                      </div>
                    )}
                    {(requestPreview.body && Object.keys(requestPreview.body).length > 0) || (requestPreview.data && Object.keys(requestPreview.data).length > 0) ? (
                      <div>
                        <span className="font-medium text-gray-600 dark:text-gray-300 block mb-1">Request body / params</span>
                        <pre className="bg-gray-50 dark:bg-gray-900 p-3 rounded overflow-x-auto font-mono text-xs">
                          {JSON.stringify(requestPreview.body || requestPreview.data || requestPreview.params || {}, null, 2)}
                        </pre>
                      </div>
                    ) : null}
                    {requestPreview.note && (
                      <p className="text-amber-700 bg-amber-50 p-2 rounded">{requestPreview.note}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Test Results */}
            {testResult && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Test Results</h3>
                
                {/* Result Summary */}
                <div className={`p-4 rounded-lg ${
                  testResult.success 
                    ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                }`}>
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-3 ${
                      testResult.success ? 'bg-green-500' : 'bg-red-500'
                    }`} />
                    <div>
                      <h4 className={`font-medium ${
                        testResult.success ? 'text-green-800' : 'text-red-800'
                      }`}>
                        {testResult.success ? 'Test Successful' : 'Test Failed'}
                      </h4>
                      <p className={`text-sm ${
                        testResult.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                        Response Status: {testResult.response_status} | 
                        Response Time: {testResult.response_time.toFixed(2)}s | 
                        Cost: {testResult.cost} coins
                      </p>
                    </div>
                  </div>
                  {testResult.success && api?.id && (
                    <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-800">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={handleAutoMapFields}
                        disabled={isAutoMapping}
                      >
                        {isAutoMapping ? 'Mapping fields...' : 'Auto-map response fields'}
                      </Button>
                      <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                        Discovers fields from this response and saves display mappings.
                      </p>
                    </div>
                  )}
                </div>

                {/* Complete Request Details */}
                <div className="space-y-3">
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3 border-b pb-2">📤 Request Details</h4>
                  
                  {/* Request Summary */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg">
                    <h5 className="text-sm font-semibold text-blue-900 mb-2">Request Summary</h5>
                    <div className="space-y-1 text-sm text-blue-800">
                      <div><span className="font-medium">Method:</span> <code className="bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">{(testResult as any).request?.method || api.requestMethod}</code></div>
                      <div><span className="font-medium">Original Value:</span> <code className="bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">{testResult.search_value}</code></div>
                      {(testResult as any).request?.formatted_value && (
                        <div><span className="font-medium">Formatted Value:</span> <code className="bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">{(testResult as any).request.formatted_value}</code></div>
                      )}
                      {(testResult as any).request?.country_code && (
                        <div><span className="font-medium">Country Code (Input):</span> <code className="bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">{(testResult as any).request.country_code}</code></div>
                      )}
                      {(testResult as any).request?.country_code_formatted && (
                        <div><span className="font-medium">Country Code (Formatted):</span> <code className="bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">{(testResult as any).request.country_code_formatted}</code></div>
                      )}
                    </div>
                  </div>

                  {/* Complete URL */}
                  <div>
                    <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Complete Request URL</h5>
                    <div className="bg-gray-900 text-green-400 p-3 rounded text-xs font-mono break-all">
                      {(testResult as any).request?.complete_url || testResult.request_url}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">This is the exact URL that was called</p>
                  </div>

                  {/* Base URL (if different) */}
                  {(testResult as any).request?.url && (testResult as any).request?.url !== (testResult as any).request?.complete_url && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Base URL</h5>
                      <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-sm font-mono break-all">
                        {(testResult as any).request.url}
                      </div>
                    </div>
                  )}

                  {/* Query Parameters (for GET requests) */}
                  {(testResult as any).request?.params && Object.keys((testResult as any).request.params).length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Query Parameters</h5>
                      <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-sm font-mono">
                        <pre>{JSON.stringify((testResult as any).request.params, null, 2)}</pre>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Parameters sent in URL query string</p>
                    </div>
                  )}

                  {/* Request Headers */}
                  <div>
                    <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Request Headers</h5>
                    <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-sm font-mono max-h-48 overflow-y-auto">
                      <pre>{JSON.stringify(testResult.request_headers, null, 2)}</pre>
                    </div>
                  </div>

                  {/* Request Body (for POST requests) */}
                  {(testResult as any).request?.body && Object.keys((testResult as any).request.body).length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Request Body (POST)</h5>
                      <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-sm font-mono">
                        <pre>{JSON.stringify((testResult as any).request.body, null, 2)}</pre>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Body type: {api.parameterFormat === 'body_json' ? 'JSON' : api.parameterFormat === 'form_data' ? 'Form Data' : 'Unknown'}
                      </p>
                    </div>
                  )}

                  {/* cURL Command */}
                  <div>
                    <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">cURL Command</h5>
                    <div className="bg-gray-900 text-green-400 p-3 rounded text-xs font-mono break-all overflow-x-auto">
                      <code>
                        {`curl -X ${(testResult as any).request?.method || api.requestMethod} "${(testResult as any).request?.complete_url || testResult.request_url}" `}
                        {testResult.request_headers && Object.entries(testResult.request_headers).map(([key, value]) => 
                          `-H "${key}: ${value}" `
                        ).join('')}
                        {(testResult as any).request?.body && Object.keys((testResult as any).request.body).length > 0 && 
                          `--data '${JSON.stringify((testResult as any).request.body)}'`
                        }
                      </code>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Copy this command to test in terminal</p>
                  </div>

                  {/* Response Details */}
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-3 border-b pb-2 mt-6">📥 Response Details</h4>
                  
                  {/* Response Summary */}
                  <div className={`p-4 rounded-lg border ${
                    testResult.success 
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                      : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                  }`}>
                    <h5 className={`text-sm font-semibold mb-2 ${
                      testResult.success ? 'text-green-900' : 'text-red-900'
                    }`}>Response Summary</h5>
                    <div className={`space-y-1 text-sm ${
                      testResult.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                      <div><span className="font-medium">Status Code:</span> <code className={`px-2 py-1 rounded ${
                        testResult.success ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'
                      }`}>{testResult.response_status}</code></div>
                      <div><span className="font-medium">Response Time:</span> <code className={`px-2 py-1 rounded ${
                        testResult.success ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'
                      }`}>{testResult.response_time.toFixed(3)}s</code></div>
                      <div><span className="font-medium">Cost:</span> <code className={`px-2 py-1 rounded ${
                        testResult.success ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'
                      }`}>{testResult.cost} coins</code></div>
                    </div>
                  </div>

                  {/* Response Body */}
                  <div>
                    <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Response Body</h5>
                    <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-sm font-mono max-h-64 overflow-y-auto">
                      <pre>{JSON.stringify(testResult.result, null, 2)}</pre>
                    </div>
                  </div>

                  {/* Error Details */}
                  {testResult.error && (
                    <div>
                      <h5 className="text-sm font-medium text-red-900 mb-2">Error Details</h5>
                      <div className="bg-red-100 dark:bg-red-900/30 p-3 rounded text-sm text-red-800">
                        {testResult.error}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <Button
            onClick={handleClose}
            variant="secondary"
            className="px-6 py-2"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};
